import { useMemo } from "react";
import type { Period } from "@/pages/admin/reports/page";

interface Product {
  id: number;
  name: string;
  image: string;
  category: string;
  marketplace: string;
  price: number;
  affiliateLink: string;
  discount: number;
}

interface TopProductsTableProps {
  products: Product[];
  period: Period;
}

const CLICK_SEEDS: Record<Period, number> = {
  "7d": 1200,
  "30d": 4800,
  "90d": 14000,
};

const MARKETPLACE_COLORS: Record<string, string> = {
  Amazon: "bg-orange-100 text-orange-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-200 text-orange-800",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
  Kabum: "bg-purple-100 text-purple-700",
};

export default function TopProductsTable({ products, period }: TopProductsTableProps) {
  const ranked = useMemo(() => {
    const seed = CLICK_SEEDS[period];
    return products
      .map((p, i) => ({
        ...p,
        clicks: Math.max(50, Math.round(seed * (1 - i * 0.08) + Math.sin(i * 1.3) * seed * 0.15)),
        ctr: parseFloat((2.5 + Math.sin(i * 0.9) * 1.8).toFixed(1)),
        hasLink: p.affiliateLink && p.affiliateLink !== "#" && p.affiliateLink.startsWith("http"),
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 8);
  }, [products, period]);

  const maxClicks = ranked[0]?.clicks || 1;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left text-xs font-semibold text-gray-400 pb-3 pr-3">#</th>
            <th className="text-left text-xs font-semibold text-gray-400 pb-3">Produto</th>
            <th className="text-left text-xs font-semibold text-gray-400 pb-3 px-3">Marketplace</th>
            <th className="text-right text-xs font-semibold text-gray-400 pb-3 px-3">Cliques</th>
            <th className="text-right text-xs font-semibold text-gray-400 pb-3 px-3">CTR</th>
            <th className="text-center text-xs font-semibold text-gray-400 pb-3">Link</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((p, idx) => {
            const barW = Math.round((p.clicks / maxClicks) * 100);
            const mpColor = MARKETPLACE_COLORS[p.marketplace] || "bg-gray-100 text-gray-600";

            return (
              <tr key={p.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                <td className="py-3 pr-3">
                  <span
                    className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-lg ${
                      idx === 0
                        ? "bg-orange-500 text-white"
                        : idx === 1
                        ? "bg-gray-200 text-gray-700"
                        : idx === 2
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {idx + 1}
                  </span>
                </td>
                <td className="py-3">
                  <div className="flex items-center gap-2.5">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-9 h-9 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate max-w-[180px]">{p.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="h-1 rounded-full bg-gray-100 flex-1 max-w-[80px] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-orange-400"
                            style={{ width: `${barW}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">{p.category}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${mpColor}`}>
                    {p.marketplace}
                  </span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className="text-sm font-bold text-gray-900">{p.clicks.toLocaleString("pt-BR")}</span>
                </td>
                <td className="py-3 px-3 text-right">
                  <span className={`text-xs font-semibold ${p.ctr >= 4 ? "text-emerald-600" : p.ctr >= 2.5 ? "text-amber-600" : "text-red-500"}`}>
                    {p.ctr}%
                  </span>
                </td>
                <td className="py-3 text-center">
                  {p.hasLink ? (
                    <span className="w-6 h-6 flex items-center justify-center mx-auto rounded-full bg-emerald-50 text-emerald-600">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                  ) : (
                    <span className="w-6 h-6 flex items-center justify-center mx-auto rounded-full bg-red-50 text-red-500">
                      <i className="ri-close-line text-sm"></i>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
